import React, { useState } from 'react';
import { Button } from '../ui/button';
import DashboardCustomizer from '../DashboardCustomizer';
import MobileMoreSheet from '../MobileMoreSheet';
import { useUnread } from '../../context/UnreadContext';
import {
  Bell, Settings, BarChart3, MessageSquare,
  LogOut, Search, Command, Menu
} from 'lucide-react';

const DashboardHeader = ({ 
  handleNavigation, isAdmin, preferences, toggleSection, 
  resetToDefaults, isSectionVisible, unreadNotifications,
  logout, onSearchClick
}) => {
  const { total: totalUnread } = useUnread();
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <header className="glass-premium sticky top-0 z-20 safe-top">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div
            className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-full bg-white flex items-center justify-center hover-lift cursor-pointer overflow-hidden"
            style={{
              border: '1.5px solid hsl(var(--gold))',
              boxShadow: '0 1px 3px rgba(40, 28, 18, 0.08), inset 0 0 0 2px hsl(var(--paper))',
              padding: '4px',
            }}
            onClick={() => handleNavigation('/')}
            data-testid="brand-logo-btn"
          >
            <img
              src="/logo-192.png"
              alt="Odys"
              className="w-full h-full object-contain"
              onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span style="font-family:Georgia,serif;font-weight:700;font-style:italic;color:hsl(var(--gold-dark));font-size:0.85rem">EO</span>'; }}
            />
          </div>
          <div className="min-w-0">
            <h1 className="font-heading font-bold text-sm sm:text-lg whitespace-nowrap truncate">
              L&apos;Équipage <span className="text-primary">Odys</span>
            </h1>
            <p className="text-[10px] sm:text-xs text-muted-foreground hidden sm:block">Tableau de bord</p>
          </div>
        </div>

        <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
          {/* Recherche */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onSearchClick}
            className="hidden sm:flex items-center gap-2 text-muted-foreground hover:text-foreground hover-lift"
            data-testid="search-btn"
          >
            <Search className="w-4 h-4" />
            <span className="text-sm">Rechercher</span>
            <kbd className="ml-2 px-1.5 py-0.5 bg-muted rounded text-[10px] flex items-center gap-0.5">
              <Command className="w-3 h-3" />K
            </kbd>
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onSearchClick}
            className="sm:hidden w-11 h-11"
            data-testid="search-btn-mobile"
          >
            <Search className="w-5 h-5" />
          </Button>

          {/* Notifications — visible partout */}
          <Button 
            variant="ghost" size="icon" 
            onClick={() => handleNavigation('/notifications')} 
            className="relative hover-lift w-11 h-11"
            data-testid="notifications-btn"
          >
            <Bell className="w-5 h-5" />
            {unreadNotifications > 0 && (
              <span
                className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                style={{ background: 'hsl(var(--red-dossier))', color: '#fff', boxShadow: '0 0 0 2px hsl(var(--paper))' }}
              >
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </Button>

          {/* Menu mobile : tout le reste */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden w-11 h-11"
            onClick={() => setMoreOpen(true)}
            data-testid="mobile-header-menu"
            aria-label="Ouvrir le menu"
          >
            <Menu className="w-5 h-5" />
          </Button>

          {/* Actions ordinateur */}
          <div className="hidden md:flex items-center gap-2">
            <DashboardCustomizer
              preferences={preferences}
              toggleSection={toggleSection}
              resetToDefaults={resetToDefaults}
              isSectionVisible={isSectionVisible}
            />
            {isAdmin && (
              <Button variant="ghost" size="sm" onClick={() => handleNavigation('/analytics')} className="hover-lift">
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </Button>
            )}
            {isAdmin && (
              <Button variant="ghost" size="sm" onClick={() => handleNavigation('/admin')} className="hover-lift">
                <Settings className="w-4 h-4 mr-2" />
                Admin
              </Button>
            )}
            <Button 
              variant="ghost" size="icon" 
              onClick={() => handleNavigation('/channels')} 
              className="relative hover-lift"
              data-testid="channels-btn"
              title="Channels"
            >
              <MessageSquare className="w-5 h-5" />
              {totalUnread > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center animate-pulse-unread"
                  style={{ background: 'hsl(var(--red-dossier))', color: '#fff', boxShadow: '0 0 0 2px hsl(var(--paper))' }}
                  data-testid="header-unread-badge"
                >
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={logout} className="hover-lift" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      <MobileMoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
    </header>
  );
};

export default DashboardHeader;
